import { Col, Row } from '@ahmdigital/ui';
import { FC } from 'react';
import { find, flow, set, update } from 'lodash/fp';

import {
  changeCoverOptionData,
  extrasOptionData,
  hospitalOptionData,
  myAccountOptionData,
  OptionData,
  somethingElseOptionData,
} from './data';
import { firstRowId } from '../../../branch';
import { GET_TOC_CONTACTS } from '../../../../../../models/table-of-contents/queries.gql';
import { SLUG } from '../../../../../../models/table-of-contents/constants';
import ContactOptions, { optionConstantIds } from '../../../contact-options';
import formatContactOptions, { CONTACT_OPTION_ID } from '../utils/format-contact-options';
import QueryType from '../../../query-type';
import RelatedQuestions from '../../../related-questions';
import useGetAllPlaceholders from '../../../../../../components/offers/logic/use-get-all-placeholders';
import useTableOfContents from '../../../../../../cms/utils/use-table-of-contents';
import type { PortableTextType } from '../../../../../../types/sanityInternal';

const AUTH_CONTACT_PHONE_NUMBER = '1300 543 058';
const AUTH_CONTACT_PHONE_NUMBER_DIGITS_ONLY = AUTH_CONTACT_PHONE_NUMBER.replaceAll(' ', '');

const replaceContactPhoneNumber = (descriptionRaw: PortableTextType[]) =>
  descriptionRaw.map((block) => ({
    ...block,
    children: block.children?.map((child) =>
      child.text?.includes('134 246')
        ? { ...child, text: child.text.replace(/134 246/g, AUTH_CONTACT_PHONE_NUMBER) }
        : child,
    ),
  }));

export type CurrentMemberOptionIDs =
  | 'loginHelp'
  | 'changeCover'
  | 'extrasAndClaiming'
  | 'hospital'
  | 'myAccount'
  | 'somethingElse';

const mapIDToOptionData: Record<CurrentMemberOptionIDs, OptionData | null> = {
  changeCover: changeCoverOptionData,
  extrasAndClaiming: extrasOptionData,
  hospital: hospitalOptionData,
  loginHelp: null,
  myAccount: myAccountOptionData,
  somethingElse: somethingElseOptionData,
};

type Props = Readonly<{
  id: CurrentMemberOptionIDs;
  showOnlyContactOptions?: boolean;
}>;

const CurrentMember: FC<Props> = ({ id, showOnlyContactOptions = false }) => {
  const data = mapIDToOptionData[id];

  const { items, loading: isLoadingTOC } = useTableOfContents({
    query: GET_TOC_CONTACTS,
    skip: false,
    slug: SLUG.CURRENT_MEMBER_CONTACTS,
    typename: 'Contact',
  });
  const { loading: isLoadingPlaceholders, placeholders } = useGetAllPlaceholders();

  const isOptionsLoaded = !isLoadingPlaceholders && !isLoadingTOC;
  const isLoginHelp = id === 'loginHelp';

  const customContactOptionId = isLoginHelp ? optionConstantIds.loginHelp : optionConstantIds.memberLogIn;
  let customTitle = 'Didn’t find what you’re after?';
  if (showOnlyContactOptions) {
    customTitle = isLoginHelp ? 'Get help' : 'Contact us';
  }

  // Adding an interim solution here for auth related inquiries - direct them to a
  // specific phone number for tracking purposes.
  let adjustedItems = items;
  const callUsItem = find({ name: 'Phone - current member' }, items);
  if (isLoginHelp && isOptionsLoaded && callUsItem) {
    const adjustedItemWithPhoneNumber = flow(
      set('item.action', `Call ${AUTH_CONTACT_PHONE_NUMBER}`),
      set('item.phoneNumber', AUTH_CONTACT_PHONE_NUMBER_DIGITS_ONLY),
    )(callUsItem);
    const adjustedItem = adjustedItemWithPhoneNumber.item.descriptionRaw?.length
      ? update('item.descriptionRaw', replaceContactPhoneNumber, adjustedItemWithPhoneNumber)
      : adjustedItemWithPhoneNumber;
    adjustedItems = [adjustedItems[0], adjustedItems[1], adjustedItem];
  }
  const contactOptions = formatContactOptions({
    customOptions: [
      {
        contactOptionId: customContactOptionId,
      },
    ],
    id: CONTACT_OPTION_ID.CURRENT_MEMBER,
    items: adjustedItems,
    placeholders,
    title: customTitle,
  });

  return (
    <>
      {!showOnlyContactOptions && data?.queryType && (
        <div id={firstRowId}>
          <Row isBoxed>
            <Col df>
              <QueryType {...data.queryType} />
            </Col>
          </Row>
        </div>
      )}
      {!showOnlyContactOptions && data?.relatedQuestions && (
        <Row isBoxed>
          <Col df>
            <RelatedQuestions {...data.relatedQuestions} />
          </Col>
        </Row>
      )}
      {isOptionsLoaded && (
        <Row isBoxed>
          <Col df>
            <ContactOptions {...contactOptions} showContactUsBanner />
          </Col>
        </Row>
      )}
    </>
  );
};

export default CurrentMember;
